
import * as THREE from "../../../libs/three.js/build/three.module.js";

// TRUE spatial nearest-neighbour search for "Snap til nærmeste punktsky
// punkt" (VectorEditingMenu.js) - user-reported bug: the distance to the
// point-cloud point must be what decides the result, not a screen-ray pick
// from the current camera (which "sees" whatever's frontmost along the
// current view direction and would silently miss a nearer point sitting
// just behind/below the target, and would change answer as the user
// orbits - see Utils.getMousePointCloudIntersection() in ../../utils.js,
// which IS exactly that kind of pick, used for mouse-driven placement,
// deliberately NOT reused here).
//
// Traverses each point cloud's GEOMETRY tree directly (pointcloud.pcoGeometry.root,
// following .children[i], gated on node.loaded) rather than the SCENE tree
// (pointcloud.visibleNodes) - the exact same choice pipeCandidates.js's own
// gatherPipeCandidates() already makes (see that file's own doc comment):
// by construction, a vertex the user could have placed sits on point-cloud
// content that was visible/loaded at some point, and the geometry tree
// doesn't depend on the CURRENT frame's frustum culling/LOD selection the
// way visibleNodes does (visibleNodes is only populated by the viewer's own
// render loop, which - unlike a menu click - has no reason to have run
// again since the vertex was placed).
//
// Point transform mirrors pipeCandidates.js's own collectFromNode()
// exactly: translate(node.boundingBox.min) composed with
// pointcloud.matrixWorld. Buffer positions are stored LOCAL to the node's
// own bounding-box corner (small numbers, full Float32 precision) rather
// than raw world (UTM-scale) coordinates - the same precision-preserving
// convention this project uses everywhere a point-cloud-scale buffer is
// built (see FeatureVisual.js's own polygon-fill fix for the general
// problem this avoids).
//
// Starts at `startRadius` and doubles up to `maxRadius`, stopping at the
// first radius tier with any candidate at all (an undersampled area needs a
// wider search sphere before anything falls inside it) - within that tier,
// the actual closest of every candidate found is returned, never just the
// first one encountered. Returns a THREE.Vector3 (world space) or null if
// nothing is within maxRadius of already-loaded content. `worldPosition`
// only needs x/y/z (a plain {x,y,z} or a THREE.Vector3 both work).
export function findNearestPointCloudPoint(pointclouds, worldPosition, startRadius = 0.25, maxRadius = 16){
	const target = new THREE.Vector3(worldPosition.x, worldPosition.y, worldPosition.z);

	for(let radius = startRadius; radius <= maxRadius; radius *= 4){
		let best = null;
		let bestDistSq = Infinity;
		const radiusSq = radius * radius;

		for(const pointcloud of pointclouds){
			if(!pointcloud.visible || !pointcloud.pcoGeometry || !pointcloud.pcoGeometry.root) continue;

			const stack = [pointcloud.pcoGeometry.root];
			while(stack.length > 0){
				const node = stack.pop();
				if(!node || !node.loaded) continue;

				// A child's bounding box is always contained within its
				// parent's (standard octree invariant) - if the parent
				// doesn't pass the spatial prune, no child could either, so
				// pruning here skips the whole subtree at once (same
				// short-circuit pipeCandidates.js's own traversal relies on).
				if(!nodeMightContainPoint(node, pointcloud.matrixWorld, target, radius)) continue;

				const found = collectNearestInNode(node, pointcloud.matrixWorld, target, radiusSq);
				if(found && found.distSq < bestDistSq){
					bestDistSq = found.distSq;
					best = found.position;
				}

				for(let i = 0; i < 8; i++){
					const child = node.children && node.children[i];
					if(child) stack.push(child);
				}
			}
		}

		if(best) return best;
	}

	return null;
}

function nodeMightContainPoint(node, matrixWorld, target, radius){
	const bbWorld = node.boundingBox.clone().applyMatrix4(matrixWorld);
	const bsWorld = bbWorld.getBoundingSphere(new THREE.Sphere());
	return bsWorld.center.distanceTo(target) <= bsWorld.radius + radius;
}

function collectNearestInNode(node, matrixWorld, target, radiusSq){
	const numPoints = node.numPoints;
	const geometry = node.geometry;
	if(!numPoints || !geometry || !geometry.attributes || !geometry.attributes.position) return null;

	const nodeMatrix = new THREE.Matrix4().makeTranslation(...node.boundingBox.min.toArray());
	const matrix = new THREE.Matrix4().multiplyMatrices(matrixWorld, nodeMatrix);

	const view = geometry.attributes.position.array;
	const pos = new THREE.Vector3();

	let best = null;
	let bestDistSq = Infinity;

	for(let i = 0; i < numPoints; i++){
		pos.set(view[i * 3], view[i * 3 + 1], view[i * 3 + 2]);
		pos.applyMatrix4(matrix);

		const distSq = pos.distanceToSquared(target);
		if(distSq <= radiusSq && distSq < bestDistSq){
			bestDistSq = distSq;
			best = pos.clone();
		}
	}

	return best ? {position: best, distSq: bestDistSq} : null;
}
